export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export const nth = (d: number) => {
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
    }
};

const getOrdinalSuffix = (day: number) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
    }
};

export const formatDateWithOrdinal = (date: number | Date) => {
    const d = new Date(date);
    const day = d.getDate();
    const year = d.getFullYear();
    const month = d.toLocaleString('en-US', { month: 'short' });
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
};

export const timeAgo = (date: number) => {
    const currentDate: any = new Date();
    const pastDate: any = new Date(date);

    const diffInSeconds = Math.ceil((currentDate - pastDate) / 1000);
    const minute = 60;
    const hour = minute * 60;
    const day = hour * 24;

    if (diffInSeconds < minute) {
        return `${diffInSeconds} seconds ago`;
    } else if (diffInSeconds < hour) {
        return `${Math.ceil(diffInSeconds / minute)}m ago`;
    } else if (diffInSeconds < day) {
        return `${Math.ceil(diffInSeconds / hour)}h ago`;
    } else {
        return `${Math.ceil(diffInSeconds / day)}d ago`;
    }
};

export const getDateOnly = (date: any) => {
    try {
        return new Date(date.toISOString().split('T')[0]);
    } catch (error) {
        return new Date(date);
    }
};

export const formatDateTime = (value: number | Date, format: string) => {
    let date = new Date(value);
    // Define format patterns
    const patterns = {
        "DD": () => String(date.getDate()).padStart(2, '0'), // Day of the month (01-31)
        "DS": () => String(date.getDate()).padStart(2, '0') + getOrdinalSuffix(date.getDate()), // Day of the month (01st-31st)
        "MMS": () => date.toLocaleString('en-US', { month: 'short' }),         // Month abbreviation (Jan-Dec)
        "MML": () => date.toLocaleString('en-US', { month: 'long' }),          // Month abbreviation (January-December)
        "YYYY": () => String(date.getFullYear()),                              // Full year (e.g., 2023)
        "YY": () => String(date.getFullYear()).slice(-2),                      // Short year (e.g., 23)
        "hh": () => String(date.getHours()).padStart(2, '0'),                  // Hours (00-23)
        "HH": () => String(date.getHours() % 12 || 12).padStart(2, '0'),       // Hours (01-12)
        "mm": () => String(date.getMinutes()).padStart(2, '0'),                // Minutes (00-59)
        "AMPM": () => date.getHours() >= 12 ? 'PM' : 'AM',                     // AM/PM
    };

    // Replace format patterns with corresponding values
    let formattedDate = format;
    for (const [pattern, replacer] of Object.entries(patterns)) {
        formattedDate = formattedDate.replace(pattern, replacer());
    }
    return formattedDate;
}

export const isNotNullUndefinedBlank = (data: any) => {
    return (data !== null && data !== undefined && data.trim() !== "");
};

export const isNotNullUndefined = (data: any) => {
    return (data !== null && data !== undefined);
};

export const isValidJSON = (jsonString: string): boolean => {
    try {
        JSON.parse(jsonString);
        return true;
    } catch (error) {
        console.warn(error);
        return false;
    }
};

export const preventEmptySpaces = (event: any) => {
    if (event.key === ' ') {
        event.preventDefault();
    }
};

export const getTimezoneDisplayName = () => {
    const formatter = new Intl.DateTimeFormat('en-US', { timeZoneName: 'long' });
    const parts = formatter.formatToParts(new Date());
    for (const part of parts) {
        if (part.type === 'timeZoneName') {
            return part.value.toString();
        }
    }
    return "UTC";
};

export const handleEmptySpaces = (event: any) => {
    const inputValue = event.target.value;
    event.target.value = inputValue.replace(/\s+/g, ' ');
};

export const filterUniqueData = (data: any, propertyName: any) => {
    const uniqueValues = new Set();
    return data.filter((item: any) => {
        const propertyValue = item[propertyName];
        if (uniqueValues.has(propertyValue)) {
            return false;
        } else {
            uniqueValues.add(propertyValue);
            return true;
        }
    });
}

export const formatNumberSystem = (value: any, system: string) => {
    // Convert the number to a string
    let numStr = value.toString() || '0';
    if (!isNaN(parseFloat(numStr))) {
        if (system === "INS") {
            // For International Numbering System
            let chars = numStr.split('');
            let result = chars.reduce((accumulator: any, char: string, index: number) => {
                // Insert comma every three digits, but not at the beginning
                if (index > 0 && (chars.length - index) % 3 === 0) {
                    accumulator += ',';
                }
                accumulator += char;
                return accumulator;
            }, '');

            return result;
        }
        else if (system === "SANS") {
            // For South Asian Numbering System
            let lastThree = numStr.slice(-3); // Take the last three digits
            let otherNumbers = numStr.slice(0, -3); // Take the digits before the last three
            if (otherNumbers !== "") {
                let chars = otherNumbers.split('');
                let result = chars.reduce((accumulator: any, char: string, index: number) => {
                    // Insert comma every two digits, but not at the beginning
                    if (index > 0 && (chars.length - index) % 2 === 0) {
                        accumulator += ',';
                    }
                    accumulator += char;
                    return accumulator;
                }, '');

                return result + "," + lastThree; // Combine with the last three digits
            } else {
                return lastThree;
            }
        }
        else if (system === "NONE") {
            // For extracting normal digits from comma-separated numbers.
            return parseFloat(value.toString().replace(/,/g, ""));
        }
        else {
            console.error("Invalid number system type.");
        }
    } else {
        console.error("Invalid value passed.");
    }
}

export const isValidDate = (date: any): boolean => {
    return date instanceof Date && !isNaN(date.getTime());
};

export const formatPrice = (value: any): string => {
    value = value ?? 0;
    if (value >= 1_000_000_000) {
        return (value / 1_000_000_000).toFixed(1) + 'B';
    } else if (value >= 1_000_000) {
        return (value / 1_000_000).toFixed(1) + 'M';
    } else if (value >= 1_000) {
        return (value / 1_000).toFixed(1) + 'K';
    } else {
        return value.toLocaleString('en-IN');
    }
};

export const isValidEmail = (value: any) => {
    // eslint-disable-next-line no-useless-escape
    const regex = /^(?!.*[._]{2})([a-zA-Z0-9_]+[._]?){1,5}[a-zA-Z0-9_]+@[a-zA-Z][a-zA-Z0-9_-]*(?<!-)\.[a-zA-Z]{2,}$/;
    return regex.test(value);
};

export const toTitleCase = (str: any) => {
    return isNotNullUndefined(str) ? str.toString()         // Convert passed data to string if it's not null/undefined 
        .toLowerCase()                   // Convert the entire string to lowercase first
        .split(/\s+/)                    // Split by one or more spaces (removes extra spaces)
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize first letter of each word
        .join(' ') : "";
};

export const getAuth0ProfileImageUrl = (url: any) => {
    return (isNotNullUndefinedBlank(url)) ? decodeURIComponent(new URL(url).searchParams.get('d') ?? (url || "")) : "";
};

export function formatNumberWithCommas(numberString: any) {
    let number = parseFloat(numberString);
    const isSmall = (Math.abs(number) < 1);
    number = isSmall ? number : Math.round(number * 100) / 100;
    let formattedNumber = isSmall ? numberString : number.toFixed(2);
    if (isSmall)
        return formattedNumber;

    const [integerPart, decimalPart] = formattedNumber.split(".");
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${formattedIntegerPart}.${decimalPart}`;
};

export function formatNumber(numberString: any) {
    let number = parseFloat(numberString);
    if (isNaN(number)) return numberString;
    const formattedNumber = Math.round(number).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return formattedNumber;
};